import NiceModal from "@ebay/nice-modal-react";
import { useEffect, useRef } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";

import Box from "components/Box";
import Button from "components/Button";
import SimpleModal from "components/modals/SimpleModal";
import NoticeTextField from "components/NoticeTextField";
import RadioGroup from "components/RadioGroup";
import SmartEditor from "components/SmartEditor";
import TitleHeading from "components/TitleHeading";
import { useGetNotice, useNewNotice, useUpdateNotice } from "hooks/apis/notice";
import { SmartEditorType } from "types/types";
import { faqTypesOptions } from "utils/const";

type FormValues = {
  type: string;
  title: string;
  author: string;
};

const SettingsFaqWritePage = () => {
  const { id } = useParams();
  const methods = useForm<FormValues>({ defaultValues: { author: "관리자" } });
  const navigate = useNavigate();
  const writeFaqMutation = useNewNotice();
  const updateFaqMutation = useUpdateNotice();
  const { data } = useGetNotice({ id: id ? +id : undefined, type: "faq" });
  const contentRef = useRef<SmartEditorType>();

  useEffect(() => {
    if (data) {
      methods.reset({
        type: data.type,
        title: data.title,
        author: data.author,
      });
      contentRef.current!.setContent(data.content);
    }
  }, [data]);

  const goToList = () => {
    navigate("/settings/faq");
  };

  const onSubmit = (form: FormValues) => {
    const content = contentRef.current!.getContent() as string;
    if (!content || content === "<p><br></p>") {
      contentRef.current!.focus();
      return;
    }

    if (id) {
      updateFaqMutation.mutate({ ...form, id: +id, content });
    } else {
      NiceModal.show(SimpleModal, {
        title: "FAQ 등록",
        message: "FAQ를 등록하시겠습니까?",
        submitText: "등록",
      })
        .then(() => {
          writeFaqMutation.mutate({ ...form, content });
        })
        .catch(() => {});
    }
  };

  return (
    <main className="p-[1.875rem] pl-[15rem] 1350:pl-[16rem] 1400:pl-[16.875rem] w-full">
      <TitleHeading
        className="mb-[0.625rem]"
        title="운영관리"
        description="서비스 운영 관리 화면입니다."
      />
      <Box className="flex justify-between mb-1">
        <h3 className="text-18 font-medium">FAQ {id ? "수정" : "등록"}</h3>
      </Box>
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <table className="table-fixed w-full border border-gray">
            <tbody>
              <tr className="border-b border-gray">
                <th className="w-[7rem] h-14 bg-lightGray px-6 text-left text-16">
                  제목
                </th>
                <td className="bg-white pl-[1.875rem] pr-5">
                  <NoticeTextField name="title" required />
                </td>
              </tr>
              <tr className="border-b border-gray">
                <th className="w-[7rem] h-14 bg-lightGray px-6 text-left text-16">
                  작성자
                </th>
                <td className="bg-white pl-[1.875rem] pr-5">
                  <NoticeTextField name="author" required />
                </td>
              </tr>
              <tr className="border-b border-gray">
                <th className="w-[7rem] h-14 bg-lightGray px-6 text-left text-16">
                  유형
                </th>
                <td className="bg-white px-[1.875rem]">
                  <RadioGroup
                    name="type"
                    options={faqTypesOptions.slice(1)}
                    required
                  />
                </td>
              </tr>
              <tr className="border-b border-gray">
                <th className="w-[7rem] align-top pt-[0.938rem] bg-lightGray px-6 text-left text-16">
                  내용
                </th>
                <td className="bg-white pl-[1.875rem] pr-5 pb-6 pt-[0.625rem]">
                  <SmartEditor ref={contentRef} defaultValue="" />
                </td>
              </tr>
            </tbody>
          </table>
          <div className="flex justify-end gap-2 mt-[1.875rem]">
            <Button outline className="w-[11rem]" onClick={goToList}>
              목록
            </Button>
            <Button
              className="w-[11rem]"
              type="submit"
              disabled={!!id && !data}
            >
              {id ? "수정" : "등록"}
            </Button>
          </div>
        </form>
      </FormProvider>
    </main>
  );
};

export default SettingsFaqWritePage;
